/**
 * Admin: gestione richieste campioni dagli agenti.
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import { Package, CheckCircle2, XCircle, Truck, Clock, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'Da approvare', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approvato', color: 'bg-blue-100 text-blue-800' },
  in_preparation: { label: 'In preparazione', color: 'bg-teal-100 text-teal-800' },
  shipped: { label: 'Spedito', color: 'bg-purple-100 text-purple-800' },
  delivered: { label: 'Consegnato', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rifiutato', color: 'bg-red-50 text-red-700' },
  cancelled: { label: 'Annullato', color: 'bg-gray-100 text-gray-600' },
};

const AdminSampleOrders = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);

  const auth = () => ({ Authorization: `Bearer ${adminToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const res = await fetch(`${API}/api/admin/sample-orders${params}`, { headers: auth() });
      if (res.ok) {
        const j = await res.json();
        setOrders(j.data || []);
        setStats(j.stats || {});
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken, filter]);

  const updateStatus = async (o, newStatus) => {
    const payload = { status: newStatus };
    if (newStatus === 'shipped') {
      const t = window.prompt('Tracking number (opzionale):');
      if (t) payload.tracking_number = t;
    }
    const res = await fetch(`${API}/api/admin/sample-orders/${o.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...auth() },
      body: JSON.stringify(payload),
    });
    if (res.ok) { toast({ title: 'Aggiornato' }); load(); setDetail(null); }
    else toast({ title: 'Errore', variant: 'destructive' });
  };

  const remove = async (o) => {
    if (!window.confirm('Eliminare la richiesta?')) return;
    const res = await fetch(`${API}/api/admin/sample-orders/${o.id}`, { method: 'DELETE', headers: auth() });
    if (res.ok) { toast({ title: 'Eliminata' }); load(); setDetail(null); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Richieste campioni</h1>
          <p className="mt-1 text-gray-600">Approva, prepara e spedisci i campioni richiesti dagli agenti.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.keys(STATUS_LABELS).map((s) => (
            <button key={s} onClick={() => setFilter(filter === s ? '' : s)} data-testid={`filter-${s}`}
              className={`text-left rounded-2xl border p-3 transition-colors ${filter === s ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300'}`}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">{STATUS_LABELS[s].label}</div>
              <div className="text-xl font-bold text-gray-900 tabular-nums">{stats[s] || 0}</div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Caricamento…</div>
          ) : orders.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <Package className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              Nessuna richiesta campioni.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-purple-50/60 text-purple-700 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">ID</th>
                  <th className="text-left px-4 py-3">Agente</th>
                  <th className="text-left px-4 py-3">Destinatario</th>
                  <th className="text-right px-4 py-3">Bottiglie</th>
                  <th className="text-center px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Richiesta</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((o) => {
                  const s = STATUS_LABELS[o.status] || { label: o.status, color: 'bg-gray-100' };
                  return (
                    <tr key={o.id} className="hover:bg-purple-50/40 cursor-pointer" onClick={() => setDetail(o)} data-testid={`sample-row-${o.id}`}>
                      <td className="px-4 py-3 font-mono text-[11px] text-purple-700">{o.id}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{o.agentName}</div>
                        <div className="text-[10px] text-gray-500">{o.agentCode}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{o.recipient_name}</div>
                        <div className="text-[10px] text-gray-500">{o.shipping_city}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{o.total_bottles}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button className="text-purple-600 hover:underline text-xs font-medium">Dettagli</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sample-detail">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold">Richiesta campioni</h2>
                <div className="text-xs text-white/80 font-mono">{detail.id}</div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase font-semibold text-gray-500">Agente</div>
                  <div className="font-medium">{detail.agentName}</div>
                  <div className="text-xs text-gray-500">{detail.agentEmail} · {detail.agentCode}</div>
                </div>
                <div>
                  <div className="text-xs uppercase font-semibold text-gray-500">Cliente</div>
                  {detail.customer ? (
                    <>
                      <div className="font-medium">{detail.customer.company || detail.customer.name}</div>
                      <div className="text-xs text-gray-500">{detail.customer.email}</div>
                    </>
                  ) : <div className="text-gray-400 italic text-xs">Uso interno agente</div>}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Destinatario</div>
                <div className="font-medium">{detail.recipient_name}</div>
                <div className="text-sm text-gray-700">{detail.shipping_address}, {detail.shipping_zipcode} {detail.shipping_city} ({detail.shipping_province})</div>
                <div className="text-xs text-gray-500">{detail.recipient_phone} · {detail.recipient_email}</div>
                {detail.shipping_notes && <div className="mt-1 text-xs text-gray-600 bg-gray-50 rounded p-2">📝 {detail.shipping_notes}</div>}
              </div>

              <div>
                <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Prodotti richiesti ({detail.total_bottles} bot.)</div>
                <ul className="border rounded-lg divide-y">
                  {detail.items.map((it, i) => (
                    <li key={i} className="px-3 py-2 flex justify-between text-sm">
                      <span>{it.liquor_name} · {it.format}</span>
                      <span className="font-mono">× {it.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {detail.reason && (
                <div>
                  <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Motivo</div>
                  <div className="text-sm text-gray-700 bg-purple-50 rounded p-2">{detail.reason}</div>
                </div>
              )}

              {detail.tracking_number && (
                <div>
                  <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Tracking</div>
                  <div className="font-mono text-sm">{detail.tracking_number}</div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex flex-wrap gap-2 justify-end">
              {detail.status === 'pending' && (
                <>
                  <button onClick={() => updateStatus(detail, 'approved')} data-testid="btn-approve"
                    className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Approva
                  </button>
                  <button onClick={() => updateStatus(detail, 'rejected')} data-testid="btn-reject"
                    className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md text-sm">
                    <XCircle className="w-4 h-4" /> Rifiuta
                  </button>
                </>
              )}
              {(detail.status === 'approved' || detail.status === 'in_preparation') && (
                <>
                  {detail.status === 'approved' && (
                    <button onClick={() => updateStatus(detail, 'in_preparation')}
                      className="inline-flex items-center gap-1 px-3 py-2 bg-teal-600 text-white rounded-md text-sm">
                      <Clock className="w-4 h-4" /> In preparazione
                    </button>
                  )}
                  <button onClick={() => updateStatus(detail, 'shipped')} data-testid="btn-ship"
                    className="inline-flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md text-sm">
                    <Truck className="w-4 h-4" /> Spedito
                  </button>
                </>
              )}
              {detail.status === 'shipped' && (
                <button onClick={() => updateStatus(detail, 'delivered')}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm">
                  <CheckCircle2 className="w-4 h-4" /> Consegnato
                </button>
              )}
              <button onClick={() => remove(detail)} className="inline-flex items-center gap-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-md text-sm">
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSampleOrders;
